import React, { useEffect, useState } from "react";
import { FaEdit } from "react-icons/fa";
import { supabase } from "../../supabaseClient";
import { PrimaryButton, SecondaryButton } from "../UI/DesignStandards";
import { notify } from "../UI/DesignStandards/Notifications/notify";
import { sanitizeOperationError } from "../../utility/errorSanitization";
import { DOUBLES_SINGLES } from "../../utility/signupSheets/signupSheetModel";
import {
  afterDoublesSummary,
  DEFAULT_DOUBLES_SINGLES_SETUP,
} from "../../utility/doublesSinglesSetup";
import {
  fetchDoublesSinglesSetup,
  saveDoublesSinglesSetupRow,
} from "../../utility/doublesSinglesSetupApi";
import {
  eventsBySquadFromRows,
  tournamentHasMixedDoublesSinglesSquad,
} from "../../utility/signupAvailability";

const DoublesSinglesSetupCard = ({
  tournamentId,
  eventsRevision = 0,
  squadsRevision = 0,
}) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(DEFAULT_DOUBLES_SINGLES_SETUP);
  const [fields, setFields] = useState(DEFAULT_DOUBLES_SINGLES_SETUP);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!tournamentId) {
        setLoading(false);
        setVisible(false);
        return;
      }
      setLoading(true);
      try {
        const { data: events, error: eventsError } = await supabase
          .from("tournamentEvents")
          .select("id, bowlerQuantity, isPrimary, isDeleted")
          .eq("tournamentID", tournamentId)
          .eq("isPrimary", true)
          .eq("isDeleted", false);
        if (eventsError) throw eventsError;

        const { data: squads, error: squadsError } = await supabase
          .from("squads")
          .select("id, isDeleted")
          .eq("tournamentID", tournamentId)
          .eq("isDeleted", false);
        if (squadsError) throw squadsError;

        let eventsBySquad = {};
        if (squads?.length) {
          const { data: extraRows, error: extraError } = await supabase
            .from("squadEvents")
            .select("squadID, eventsID")
            .in(
              "squadID",
              squads.map((squad) => squad.id)
            );
          if (!extraError) {
            eventsBySquad = eventsBySquadFromRows(extraRows || []);
          }
        }

        if (cancelled) return;
        const mixed = tournamentHasMixedDoublesSinglesSquad(
          squads || [],
          events || [],
          eventsBySquad
        );
        setVisible(mixed);
        if (mixed) {
          const next = await fetchDoublesSinglesSetup(tournamentId);
          if (cancelled) return;
          setSaved(next);
          setFields(next);
        }
        setIsEditing(false);
      } catch (error) {
        if (!cancelled) {
          setVisible(false);
          notify.error(
            sanitizeOperationError.read(error, "Doubles and Singles")
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [tournamentId, eventsRevision, squadsRevision]);

  const handleStartEdit = () => {
    setFields(saved);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setFields(saved);
    setIsEditing(false);
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const next = await saveDoublesSinglesSetupRow(tournamentId, {
        shareSquad: true,
        afterDoubles: fields.afterDoubles,
      });
      setSaved(next);
      setFields(next);
      setIsEditing(false);
    } catch (error) {
      notify.error(sanitizeOperationError.save(error, "Doubles and Singles"));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !visible) return null;

  const display = isEditing ? fields : saved;

  return (
    <div className="card-board p-6 mt-6">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="heading-display text-xl">Doubles and Singles</h2>
        </div>
        {!isEditing ? (
          <SecondaryButton
            type="button"
            onClick={handleStartEdit}
            icon={FaEdit}
            className="!px-3 !py-2 text-sm flex-shrink-0"
          >
            Edit
          </SecondaryButton>
        ) : null}
      </div>

      {isEditing ? (
        <form onSubmit={handleSave} className="card-inset p-4 space-y-6">
          <fieldset className="space-y-2">
            <legend className="block text-sm font-medium text-brand-charcoal mb-2">
              After doubles, they bowl singles?
            </legend>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="after-doubles"
                className="mt-1"
                checked={fields.afterDoubles === DOUBLES_SINGLES.optional}
                onChange={() =>
                  setFields((prev) => ({
                    ...prev,
                    afterDoubles: DOUBLES_SINGLES.optional,
                  }))
                }
              />
              <span>Only if they mark it</span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="after-doubles"
                className="mt-1"
                checked={fields.afterDoubles === DOUBLES_SINGLES.always}
                onChange={() =>
                  setFields((prev) => ({
                    ...prev,
                    afterDoubles: DOUBLES_SINGLES.always,
                  }))
                }
              />
              <span>Everyone</span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="radio"
                name="after-doubles"
                className="mt-1"
                checked={fields.afterDoubles === DOUBLES_SINGLES.doublesOnly}
                onChange={() =>
                  setFields((prev) => ({
                    ...prev,
                    afterDoubles: DOUBLES_SINGLES.doublesOnly,
                  }))
                }
              />
              <span>Not on the Doubles signup sheet</span>
            </label>
          </fieldset>

          <p className="text-sm text-brand-muted">
            Print signups and Import signups use this. Only if they mark it
            prints a Singles column. Everyone prints Doubles then Singles with
            no Singles column.
          </p>

          <div className="flex flex-wrap gap-2">
            <PrimaryButton type="submit" loading={saving}>
              Save
            </PrimaryButton>
            <SecondaryButton type="button" onClick={handleCancel} disabled={saving}>
              Cancel
            </SecondaryButton>
          </div>
        </form>
      ) : (
        <div className="card-inset p-4">
          <div className="text-xs font-medium text-brand-muted mb-1">
            After doubles, they bowl singles?
          </div>
          <div className="text-sm text-brand-charcoal">
            {afterDoublesSummary(display)}
          </div>
        </div>
      )}
    </div>
  );
};

export default DoublesSinglesSetupCard;
